/**
 * useHoursManager Hook
 * Manages working hours state and operations
 */

import { useState, useCallback } from 'react';

const DAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

export const useHoursManager = (initialHours = {}) => {
  const [workingDaysAndHours, setWorkingDaysAndHours] = useState(initialHours);

  /**
   * Update hours for a specific day
   */
  const handleHoursChange = useCallback((day, field, value) => {
    setWorkingDaysAndHours(prev => ({
      ...prev,
      [day]: {
        ...(typeof prev[day] === 'object' ? prev[day] : {}),
        [field]: value,
      },
    }));
  }, []);

  /**
   * Apply same hours to all days
   */
  const applyHoursToAllDays = useCallback((openTime, closeTime) => {
    if (openTime && closeTime) {
      const newHours = {};
      DAYS.forEach(day => {
        newHours[day] = { open: openTime, close: closeTime };
      });
      setWorkingDaysAndHours(newHours);
    }
  }, []);

  /**
   * Format time string to HH:MM format
   */
  const formatTime = useCallback((timeStr) => {
    if (!timeStr) return '';
    const parts = timeStr.split(':');
    if (parts.length === 2) {
      return `${parts[0].padStart(2, '0')}:${parts[1].padStart(2, '0')}`;
    }
    return timeStr;
  }, []);

  /**
   * Get hours for a specific day
   */
  const getDayHours = useCallback((day) => {
    const hours = workingDaysAndHours[day];
    if (!hours) return { open: '', close: '' };
    return {
      open: formatTime(hours.open || ''),
      close: formatTime(hours.close || ''),
    };
  }, [workingDaysAndHours, formatTime]);

  /**
   * Check if business is open on a specific day
   */
  const isOpenOnDay = useCallback((day) => {
    const hours = workingDaysAndHours[day];
    return !!(hours?.open && hours?.close);
  }, [workingDaysAndHours]);

  /**
   * Reset hours to initial state
   */
  const resetHours = useCallback(() => {
    setWorkingDaysAndHours(initialHours);
  }, [initialHours]);

  return {
    workingDaysAndHours,
    setWorkingDaysAndHours,
    handleHoursChange,
    applyHoursToAllDays,
    formatTime,
    getDayHours,
    isOpenOnDay,
    resetHours,
    DAYS,
  };
};
